// ============================================================================
// L'ORDRE NATUREL DES TAILLES — celui qu'un vendeur attend quand personne n'a rangé la grille à la main.
//
// UTILITÉ : une grille née d'une reprise n'a pas d'ordre humain. Sans règle, ses valeurs sortent dans
// l'ordre de l'alphabet — « 100 » avant « 25 », « L » avant « M » avant « S », « XL » avant « XS ».
// Cette règle range ce qu'elle RECONNAÎT (les nombres, les tailles alpha, les tailles intermédiaires
// comme « XS-S ») et laisse le reste dans un ordre lisible, sans jamais rien refuser : une taille
// qu'elle ne comprend pas est rangée après, pas perdue.
//
// ⚠️ ELLE NE REMPLACE PAS L'ORDRE D'UNE GRILLE RANGÉE : `size.rank` reste la vérité. Elle sert à POSER
// un premier rang à ce qui n'en a pas ; la reprise ne réordonne jamais une grille existante, elle
// AJOUTE derrière ce qui est déjà rangé.
// ⚠️ SANS LOCALE, Y COMPRIS POUR CE QU'ELLE NE RECONNAÎT PAS : la caisse (Hermes) et le serveur (Node) la lisent
// toutes deux — l'ordre des déclinaisons d'une source, le panneau de filtres — et leurs collateurs ne
// rangent pas pareil (Forme FI).
// ============================================================================
import { compareCodes, compareNaturalKeys, naturalKey, type NaturalKey } from './natural';

/** Ce que la règle a reconnu dans une valeur, et la clé qui la range dans son groupe. */
interface SizeKey {
  /** 0 = nombre · 1 = taille alpha (ou intermédiaire) · 2 = tout le reste. Les groupes se suivent. */
  group: 0 | 1 | 2;
  key: number;
}

/** Les synonymes de la taille unique — tous rangés APRÈS les tailles alpha. */
const ONE_SIZE = new Set(['TU', 'U', 'OS', '1SIZE', 'ONESIZE', 'ONE SIZE', 'UNIQUE', 'TAILLE UNIQUE']);
const ONE_SIZE_KEY = 100;

/**
 * La position d'une taille alpha sur l'axe : … XXS = −3, XS = −2, S = −1, M = 0, L = 1, XL = 2, XXL = 3 …
 * ⚠️ « 3XL » ET « XXXL » SONT LA MÊME POSITION : les deux graphies coexistent chez les fournisseurs, et
 *    une grille qui les range à deux endroits différents mentirait sur l'ordre.
 */
function alphaKey(token: string): number | null {
  const t = token.toUpperCase().replace(/\s+/g, ' ').trim();
  if (t === 'M') return 0;
  if (ONE_SIZE.has(t)) return ONE_SIZE_KEY;
  const m = /^(\d*)(X*)(S|L)$/.exec(t);
  if (!m) return null;
  const digits = m[1] ? Number(m[1]) : 0;
  const xs = m[2]!.length;
  // « 3XL » = trois X : le chiffre REMPLACE la répétition, un seul X reste ; « XXXL » = trois X ;
  // « XL » = un X ; « S » = zéro X. « 3L » ou « 2XXL » ne sont pas des graphies connues — on ne devine pas.
  if (digits > 0 && xs !== 1) return null;
  const count = digits > 0 ? digits : xs;
  return m[3] === 'L' ? count + 1 : -(count + 1);
}

/** Les jetons alpha, du plus long au plus court — la découpe gloutonne de « XLXXL » en XL + XXL en dépend. */
const ALPHA_TOKENS = ['XXXXL', 'XXXL', 'XXL', 'XL', 'L', 'M', 'S', 'XS', 'XXS', 'XXXS', '4XL', '3XL', '2XL', '2XS', '3XS']
  .sort((a, b) => b.length - a.length);

/**
 * Une taille INTERMÉDIAIRE (« XS-S », « M/L », « XSS », « XLXXL ») se range ENTRE ses deux bornes.
 * ⚠️ Deux bornes exactement, toutes deux reconnues : « S-M-L » ou « S-40 » ne sont pas des intermédiaires.
 */
function intermediateKey(raw: string): number | null {
  const t = raw.toUpperCase().trim();
  const parts = t.split(/[-/ ]+/).filter(Boolean);
  let bornes: string[] | null = null;
  if (parts.length === 2) bornes = parts;
  else if (parts.length === 1) {
    // Deux jetons collés (« SM », « XLXXL ») : on essaie chaque coupe en deux, la première qui donne
    // deux jetons connus l'emporte.
    for (const a of ALPHA_TOKENS) {
      if (!t.startsWith(a) || t.length === a.length) continue;
      const reste = t.slice(a.length);
      if (alphaKey(reste) !== null && alphaKey(a) !== null) { bornes = [a, reste]; break; }
    }
  }
  if (!bornes) return null;
  const k1 = alphaKey(bornes[0]!);
  const k2 = alphaKey(bornes[1]!);
  if (k1 === null || k2 === null || k1 === ONE_SIZE_KEY || k2 === ONE_SIZE_KEY) return null;
  return (k1 + k2) / 2;
}

/** La clé de rangement d'une valeur — nombre, taille alpha, ou « autre ». */
function sizeKey(raw: string): SizeKey {
  const v = raw.trim();
  const num = /^(\d+)(?:[.,](\d+))?$/.exec(v);
  if (num) return { group: 0, key: Number(`${num[1]}.${num[2] ?? '0'}`) };
  const alpha = alphaKey(v);
  if (alpha !== null) return { group: 1, key: alpha };
  const inter = intermediateKey(v);
  if (inter !== null) return { group: 1, key: inter };
  return { group: 2, key: 0 };
}

/** Une taille prête à ranger : son groupe, sa clé, et pour « le reste » sa forme d'ordre naturel. */
export interface PreparedSize {
  raw: string;
  trimmed: string;
  group: 0 | 1 | 2;
  key: number;
  /** Renseignée pour le groupe 2 seulement — ce que la règle ne reconnaît pas se lit dans l'ordre naturel. */
  natural: NaturalKey | null;
}

/** Prépare une valeur de taille une fois — un tri de milliers de valeurs ne la relit pas à chaque comparaison. */
export function prepareSize(raw: string): PreparedSize {
  const k = sizeKey(raw);
  const trimmed = raw.trim();
  return { raw, trimmed, group: k.group, key: k.key, natural: k.group === 2 ? naturalKey(trimmed) : null };
}

/**
 * Compare deux tailles préparées : les nombres croissants, puis les tailles alpha de la plus petite à la plus grande
 * (taille unique en dernier), puis le reste dans l'ordre naturel, sans locale.
 * ⚠️ Stable et TOTAL : deux valeurs distinctes ne sont jamais « égales », sinon l'ordre dépendrait de l'ordre
 *    d'arrivée — et deux reprises donneraient deux grilles différentes.
 */
export function comparePreparedSizes(a: PreparedSize, b: PreparedSize): number {
  if (a.group !== b.group) return a.group - b.group;
  if (a.group !== 2 && a.key !== b.key) return a.key - b.key;
  if (a.group === 2) {
    const l = compareNaturalKeys(a.natural!, b.natural!);
    if (l !== 0) return l;
  }
  // ⚠️ LE DÉPARTAGE NE DÉPEND PAS DE LA LOCALE : deux graphies de même rang (« 3XL » / « XXXL », « 41.5 » /
  //    « 41,5 ») se rangent par longueur puis par point de code, pareil sur toute machine.
  return a.trimmed.length - b.trimmed.length || compareCodes(a.trimmed, b.trimmed) || compareCodes(a.raw, b.raw);
}

/** Compare deux valeurs de taille dans l'ordre naturel (`comparePreparedSizes`, sur des valeurs brutes). */
export function compareSizeValues(a: string, b: string): number {
  return comparePreparedSizes(prepareSize(a), prepareSize(b));
}

/** Les valeurs rangées dans l'ordre naturel — un nouveau tableau, l'entrée n'est pas touchée. */
export function sortSizeValues(values: readonly string[]): string[] {
  return values.map((v) => ({ v, k: prepareSize(v) })).sort((x, y) => comparePreparedSizes(x.k, y.k)).map((x) => x.v);
}
